<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import { EditorState } from "@common/Common"
    import type { ProjectPlanTemplateEditorViewModel } from "@Quest/ProjectPlanTemplates/Components/ProjectPlanTemplateEditor"

    // props
    const props = defineProps<{
        viewModel: ProjectPlanTemplateEditorViewModel
    }>()

    // emits
    const emit = defineEmits<{
        (e: 'create-template', payload: any): void
        (e: 'update-template', payload: any): void
    }>()

    // vm shortcut
    const vm = toRef(props, 'viewModel')

    // state
    const opened = computed(() => vm.value?.opened ?? false)

    const confirmButtonLabel = computed(() =>
        vm.value.state === EditorState.Creator ? 'Create' : 'Update'
    )

    const modalTitle = computed(() =>
        vm.value.state === EditorState.Creator
            ? 'New business case template'
            : 'Edit the business case template'
    )

    // actions
    function onFormSubmit()
    {
        if (vm.value.state === EditorState.Creator)
        {
            emit('create-template', vm.value.data)
        }
        else if (vm.value.state === EditorState.Editor)
        {
            emit('update-template', vm.value.data)
        }
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ modalTitle }}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <form id="project-plan-template-editor-form" @submit.prevent="onFormSubmit">
                            <div class="form-group">
                                <label>Name</label>
                                <input type="text" v-model.trim="vm.data.Name" class="form-control" required minlength="2" maxlength="100" />
                            </div>

                            <div class="form-group">
                                <label>Description</label>
                                <textarea v-model.trim="vm.data.Description" class="form-control" maxlength="250" />
                            </div>

                            <hr />

                            <div class="form-group">
                                <label for="account">Granularity</label>
                                <select id="account" v-model="vm.data.Granularity" class="form-control">
                                    <option value="Monthly">Monthly</option>
                                    <option value="Quarterly">Quarterly</option>
                                    <option value="Yearly">Yearly</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label for="denomination">Currency denomination</label>
                                <select id="denomination" v-model="vm.data.Denomination" class="form-control">
                                    <option value="Unit">Unit</option>
                                    <option value="Thousand">Thousand</option>
                                    <option value="Million">Million</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label>Project execution (in months)</label>
                                <input type="number" v-model.number="vm.data.DurationInMonths" class="form-control" required min="1" step="1" />
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="project-plan-template-editor-form" class="btn btn-primary">{{ confirmButtonLabel }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
